import { React, useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  IconButton,
  Typography,
  AppBar,
  Toolbar,
  Stack,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ComparisonSettings from "./ComparisonSettings";

import { useFormik } from "formik";
import { SectionBox } from "../HelperFunctions/HelperFunctions";
import { getUserCustomizedData } from "../API/backend";
import { Loader } from "../HelperFunctions/HelperFunctions";
import { enqueueSnackbar } from "notistack";
import CustomeDetails from "./CustomeDetails";
import { useLoaderData } from "react-router-dom";
import { handleSessionOut } from "../HelperFunctions/HelperFunctions";

const CustomOptions = ({
  type,
  width = "150px",
  showSelect = false,
  selected,
  onClick,
  disabled,
  selectedValue,
  onSelectChange,
}) => {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Button
        size="small"
        variant="contained"
        endIcon={
          <CheckCircleIcon
            style={{
              color: disabled ? "#d7d7d7" : selected ? "#00ba00" : "#d7d7d7",
            }}
          />
        }
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 400,
          fontSize: "11px",
          border: selected ? "0.1px solid #f6ffe6" : "0.1px solid #d7d7d7",
          backgroundColor: disabled
            ? "#f0f0f0"
            : selected
              ? "#f6ffe6"
              : "#ffffff",
          color: disabled ? "#a0a0a0" : "#333333",
          borderRadius: "3px",
          textTransform: "none",
          width: width,
          padding: "2px 10px",
          whiteSpace: "nowrap",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
        onClick={disabled ? undefined : onClick}
        disableRipple
        disableElevation
      >
        <span style={{ flexGrow: 1, textAlign: "left", overflow: "hidden" }}>
          {type}
        </span>
      </Button>
      {selected && showSelect && !disabled && (
        <Select
          size="small"
          // defaultValue="Both"
          value={selectedValue}
          onChange={(e) => onSelectChange(e.target.value)}
          style={{ width: "100px", height: "25px", fontSize: "10px" }}
        >
          <MenuItem value="both">Both</MenuItem>
          <MenuItem value="last_year">Last Year</MenuItem>
          <MenuItem value="budget">Budget</MenuItem>
          <MenuItem value="actual">None</MenuItem>
        </Select>
      )}
    </Stack>
  );
};

const CustomSelect = ({ label, name, formik, options, width = 280 }) => {


  const [previousSettings, setPreviousSettings] = useState({
    column_settings: {},
    selectedOption: "",
  });

  const [sideBySideSettings, setSideBySideSettings] = useState({
    column_settings: {},
    selectedOption: "",
  });

  return (
    <FormControl sx={{ m: 0.5, width }} size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        name={name}
        value={formik.values.layout_settings[name]}
        onChange=
        {(event) => {
          const tabs = ["sale_date", "month_to_date", "year_to_date"];
          const value = event.target.value;
          formik.setFieldValue(`layout_settings.${name}`, value);

          if (value === "Sale date / MTD / YTD - Side by side") {

            const defaultKeys = ["custom_occupancy", "custom_adr"];

            const skipKeys = [
              "custom_sale_date",
              "custom_month_to_date",
              "custom_year_to_date",
            ];

            const savedColumns = {};
            tabs.forEach((tab) => {
              savedColumns[tab] = { ...formik.values.column_settings[tab] };
            });

            setPreviousSettings({
              column_settings: savedColumns,
              selectedOption: formik.values.layout_settings.selectedOption || "",
            });

            if (Object.keys(sideBySideSettings.column_settings).length > 0) {
              tabs.forEach((tab) => {
                formik.setFieldValue(`column_settings.${tab}`, sideBySideSettings.column_settings[tab]);
              });
              formik.setFieldValue("layout_settings.selectedOption", sideBySideSettings.selectedOption || "");
              return;
            }

            ["sale_date", "month_to_date", "year_to_date"].map((tab) => {
              const tabValues = { ...formik.values.column_settings[tab] };
              const customKeys = Object.keys(tabValues).filter(
                (k) => k.startsWith("custom_") && tabValues[k]
              );

              if (customKeys.length > 3) {
                customKeys
                  .filter((k) => !skipKeys.includes(k) && !defaultKeys.includes(k))
                  .forEach((k) => delete tabValues[k]);

                defaultKeys.forEach((key) => {
                  if (!tabValues[key]) {
                    tabValues[key] = "both";
                  }
                });

                formik.setFieldValue(`column_settings.${tab}`, tabValues);

              }
            });

            formik.setFieldValue("layout_settings.selectedOption", "");
          }

          if (value === "Sale date / MTD / YTD - Seperate rows"
            && Object.keys(previousSettings.column_settings).length > 0
          ) {
            const savedSideBySide = {};
            tabs.forEach((tab) => {
              savedSideBySide[tab] = { ...formik.values.column_settings[tab] };
            });
            setSideBySideSettings({
              column_settings: savedSideBySide,
              selectedOption: "",
            });

            tabs.forEach((tab) => {
              formik.setFieldValue(`column_settings.${tab}`, previousSettings.column_settings[tab] || {});
            });

            formik.setFieldValue("layout_settings.selectedOption", previousSettings.selectedOption || "");
          }
        }}
      >
        {options.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
};

const CustomizeDialog = ({
  open,
  onClose,
  handleData,
  data,
  handleCustomeLayout,
  defaultDetails,
  exportFormate
}) => {

  const { clientName, UID, clientID, token, userPreferences } = useLoaderData();
  const [loading, setLoading] = useState(false);
  const yesterDate = new Date(Date.now() - 86400000).toISOString();



  const handleFormSubmit = async (values) => {


    const details = values.details;

    if (!details.management_groups || Object.keys(details.management_groups).length === 0) {
      enqueueSnackbar("Please select a Management Group.", { variant: "info" });
      return;
    }

    if (!details.corporation || details.corporation.length === 0) {
      enqueueSnackbar("Please select at least one Corporation.", { variant: "info" });
      return;
    }

    if (!details.date) {
      enqueueSnackbar("Please select a Date.", { variant: "info" });
      return;
    }


    let payload = { ...values };
    payload["column_settings"] = await removePrefixes(payload["column_settings"]);
    payload["column_settings"] = await removeSettingKeys(payload["column_settings"]);

    if (
      shouldValidate(payload.column_settings)
    ) {

      enqueueSnackbar("Please select at least one column to generate the report", { variant: "warning" })
      return;
    }
    setLoading(true);
    try {
      if (payload.layout_settings.type === "Sale date / MTD / YTD - Side by side") {
        payload.layout_settings = {
          ...payload.layout_settings,
          combinedPages: false,
        };
      };
      const isCorpLegalName = userPreferences.IsShowCorpLegalName
      const getCustomizedData = await getUserCustomizedData(
        payload,
        clientName,
        clientID,
        UID,
        isCorpLegalName


      );
      if (getCustomizedData.status === 200) {
        const result = await getCustomizedData.json();
        exportFormate(result)
        const clonedResult = JSON.parse(JSON.stringify(result));
        handleData(clonedResult);

      } else if (getCustomizedData.status === 401) {
        await handleSessionOut()
      }
      // else {
      //   enqueueSnackbar(getCustomizedData.detail, { variant: "error" });
      //   const result = await getCustomizedData.json();
      //   handleData(result);
      // }
    } catch (error) {
      enqueueSnackbar("Error fetching data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCustomeData = async () => {

    let customeData = { ...formik.values };
    customeData["column_settings"] = await removePrefixes(customeData["column_settings"]);
    customeData["column_settings"] = await removeSettingKeys(customeData["column_settings"]);
    if (
      shouldValidate(customeData.column_settings)
    ) {
      enqueueSnackbar("Please select at least one column to generate the report", { variant: "warning" })
      return;
    };

    if (
      customeData.layout_settings.type === "Sale date / MTD / YTD - Side by side" ||
      (formik.values.layout_settings.export != "PDF"
        && customeData.layout_settings.type === "Sale date / MTD / YTD - Seperate rows")
    ) {

      customeData.layout_settings = {
        ...customeData.layout_settings,
        combinedPages: false,
      };
    };
    // const customeData = removePrefixes(formik.values);
    const { ly_budget_values, ...filteredColumnSettings } =
      customeData.column_settings;

    const data = {
      only_variance: customeData.column_settings.ly_budget_values,
      layout_settings: customeData.layout_settings,
      column_settings: filteredColumnSettings,
      details: customeData.details,
      corporations: customeData.details.corporation,
      date: customeData.details.date,
    };
    handleCustomeLayout(data);
    onClose();
  };

  const removeSettingKeys = (obj) => {
    Object.keys(obj).forEach(key => {
      if (typeof obj[key] !== 'object' || obj[key] === null) return;

      const entries = Object.entries(obj[key]);
      const trueCount = entries.filter(([_, v]) => v === true).length;
      const allEmptyOrTrue = entries.every(([_, v]) => v === "" || v === true);

      if (trueCount <= 2 && allEmptyOrTrue) obj[key] = {};
    });
    return obj;
  };

  const removePrefixes = (obj) =>
    Object.fromEntries(
      Object.entries(obj)
        .filter(
          ([key]) =>
            ![
              "custom_sale_date",
              "custom_month_to_date",
              "custom_year_to_date",
            ].includes(key)
        )
        .map(([key, value]) => [
          key.replace(/^(custom_|statis1_|statis2_)/, ""),
          value && typeof value === "object" ? removePrefixes(value) : value,
        ])
    );
  const shouldValidate = (columnSettings = {}) => {
    const validateLabels = ["occupancy", "adr", "rev_par", "room_revenue", "other_revenue", "total_revenue", "avail_rooms", "sold_rooms", "ooo_rooms"];
    const sections = ['sale_date', 'month_to_date', 'year_to_date'];

    return sections.every(section => {
      const data = columnSettings[section] || {};
      return !validateLabels.some(key => key in data && Boolean(data[key]));
    });
  }



  const getDefaults = (key, extra = {}) => {
    const keyMap = { mtd: "month_to_date", ytd: "year_to_date" };
    const actualKey = keyMap[key] || key;
    const values = data?.column_settings?.[actualKey] || {};
    // console.log("actualKey", actualKey)
    // console.log("getDefaults", values)
    const validPairsCount = Object.entries(values).filter(
      ([_, value]) => value !== false && value !== ""
    ).length;

    return {
      [`custom_${actualKey}`]:
        // Object.values(values).some((v) => v) 
        // Object.keys(values).length > 2 && Object.values(values).some(v => v)
        //   ? "both"
        //   : "",
        validPairsCount > 2 ? "both" : "",
      statis1_avail_rooms: values.avail_rooms || "",
      statis1_sold_rooms: values.sold_rooms || "",
      statis1_ooo_rooms: values.ooo_rooms || "",
      custom_occupancy: values.occupancy || "",
      custom_adr: values.adr || "",
      custom_rev_par: values.rev_par || "",
      custom_room_revenue: values.room_revenue || "",
      custom_other_revenue: values.other_revenue || "",
      custom_total_revenue: values.total_revenue || "",
      // statis2_last_year: values.last_year || "",
      // statis2_budget: values.budget || "",
      ...Object.fromEntries(Object.keys(extra).map(k => [k, values[k] || true])),
    };
  };

  const sale_date = {
    label: true,
    display_date: true
  };
  const month_to_date = {
    MonthFromDate: true,
    MonthToDate: true,
  };
  const year_to_date = {
    YearFromDate: true,
    YearToDate: true
  };

  const formik = useFormik({
    initialValues: {
      details: {
        management_groups: (defaultDetails && data?.details?.management_groups) || {
          "DisplayText": "--ALL--",
          "ValueCode": "ALL"
        },
        corporation: (defaultDetails && data?.details?.corporation) || [],
        date: (defaultDetails && data?.details?.date) || yesterDate,
      },
      layout_settings: {
        type:
          data?.layout_settings?.type || "Sale date / MTD / YTD - Seperate rows",
        export: data?.layout_settings?.export || "PDF",
        selectedOption:
          data?.layout_settings?.selectedOption ||
          // "Sale date / MTD / YTD - Seperate rows",
          "Seperate by KPIs",
        combinedPages: data?.layout_settings?.combinedPages || false
      },
      column_settings: {
        sale_date: getDefaults("sale_date", sale_date),
        month_to_date: getDefaults("mtd", month_to_date),
        year_to_date: getDefaults("ytd", year_to_date),
        ly_budget_values:
          data?.only_variance ||
          "",
      },
    },
    onSubmit: handleFormSubmit,
  });

  const layoutOptions = [
    {
      value: "Sale date / MTD / YTD - Side by side",
      label: "Sale date / MTD / YTD - Side by side",
    },
    {
      value: "Sale date / MTD / YTD - Seperate rows",
      label: "Sale date / MTD / YTD - Seperate rows",
    },
  ];

  const exportOptions = [
    { value: "Excel", label: "Excel" },
    { value: "PDF", label: "PDF" },
    // { value: "Mail Body", label: "Mail Body" },
  ];
  const comparisonOptions = [
    "Seperate by Actual / LY / Budget",
    "Seperate by KPIs",
  ];
  return (
    <Dialog
      open={open}
      // maxWidth="md"
      fullScreen
      sx={{
        "& .MuiDialog-paper": {
          minWidth: "1250px",
          width: "80%",
          height: "95%",
          overflow: "auto",
        },
      }}
      BackdropProps={
        {
          // sx: { backgroundColor: "transparent" },
        }
      }
      PaperProps={{
        elevation: 0,
        sx: {
          border: "1px solid rgba(0, 0, 0, 0.12)",
        },
      }}
    >

      <Toolbar sx={{ bgcolor: "#d7d7d7" }}>
        <IconButton
          size="small"
          edge="start"
          onClick={onClose}
          aria-label="close"
          style={{ backgroundColor: "#686868" }}
          disableRipple
        >
          <CloseIcon sx={{ color: "white" }} />
        </IconButton>
        <Typography
          sx={{
            ml: 2,
            flex: 1,
            color: "#1f1f1f",
          }}
          component="div"
          fontWeight={600}
          fontSize={"16px"}
        >
          Customizations
        </Typography>
        <Stack direction={"row"} gap={2}>
          <Button
            size="small"
            disableRipple
            disableElevation
            variant="contained"
            sx={{
              px: 4,
              textTransform: "none",
              backgroundColor: "#b5dcf2",
              color: "#000",
            }}
            onClick={onClose}
          >
            Cancel
          </Button>
          {typeof handleData === "function" && (
            <Button
              size="small"
              disableRipple
              disableElevation
              variant="contained"
              sx={{
                px: 4,
                textTransform: "none",
                backgroundColor: "#b5dcf2",
                color: "#000",
              }}
              // onClick={onClose}
              type="submit"
              onClick={formik.handleSubmit}
            >
              Generate Report
            </Button>
          )}
          {typeof handleCustomeLayout === "function" && (
            <Button
              size="small"
              disableRipple
              disableElevation
              variant="contained"
              sx={{
                px: 4,
                textTransform: "none",
                backgroundColor: "#b5dcf2",
                color: "#000",
              }}
              // onClick={onClose}
              type="submit"
              onClick={handleCustomeData}
            >
              Save
            </Button>
          )}
        </Stack>
      </Toolbar>

      <DialogContent id="dialog-content" sx={{ position: 'relative' }} >
        <form onSubmit={formik.handleSubmit}>
          <Stack
            direction="row"
            // height="calc(100vh - 130px)"
            // minHeight={"100%"}
            width={"100%"}
            gap={1}

          >
            <Stack width={"45%"} gap={5} >
              {typeof handleCustomeLayout != "function" &&
                <SectionBox height={"100%"} title="Details"  >
                  <CustomeDetails formik={formik} handleData={handleData} onClose={onClose} defaultDetails={defaultDetails} />
                </SectionBox>}

              <SectionBox height="100%" title="Layout Settings">
                <Stack direction={"column"} gap={3}>
                  <Stack mt={2} gap={2}>
                    <CustomSelect
                      label="Layout Type"
                      name="type"
                      formik={formik}
                      options={layoutOptions}
                    />

                    {formik.values.layout_settings.type ===
                      "Sale date / MTD / YTD - Seperate rows" && (
                        <Stack direction="row" gap={1}>
                          {comparisonOptions.map((option) => (
                            <CustomOptions
                              key={option}
                              type={option}
                              width="222px"
                              selected={
                                formik.values.layout_settings.selectedOption ===
                                option
                              }
                              onClick={() =>
                                formik.setFieldValue(
                                  "layout_settings.selectedOption",
                                  option
                                )
                              }
                            />
                          ))}
                        </Stack>
                      )}
                  </Stack>
                  {typeof handleCustomeLayout === "function" &&
                    <CustomSelect
                      label="Export"
                      name="export"
                      formik={formik}
                      options={exportOptions}
                      width={150}
                    />}
                  {(formik.values.layout_settings.type === "Sale date / MTD / YTD - Seperate rows" &&
                    formik.values.layout_settings.export === "PDF") &&
                    <CustomOptions
                      key="combinedPages"
                      type="Single/Combined PDF Export"
                      width="222px"
                      selected={formik.values.layout_settings.combinedPages}
                      onClick={() =>
                        formik.setFieldValue(
                          "layout_settings.combinedPages",
                          !formik.values.layout_settings.combinedPages
                        )
                      }
                    />
                  }
                </Stack>
              </SectionBox>
            </Stack>
            <Box width={"65%"}>
              <SectionBox height="100%" title="Columns and Comparison Settings">
                <ComparisonSettings
                  CustomOptions={CustomOptions}
                  formik={formik}
                />
              </SectionBox>
            </Box>
          </Stack>
        </form>
      </DialogContent>
      <Loader loading={loading} />
    </Dialog>
  );
};

export default CustomizeDialog;
